import { useCallback, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useApps } from "@/api/useApps";
import { ChartVersionsPage } from "@/components/ChartVersionsPage";
import {
  CHART_UPDATES_AVAILABLE,
  parseVersionSearch,
  type VersionSearch,
} from "@/lib/version-search";

interface VersionsSearchInput {
  projects?: string;
  namespaces?: string;
  view?: typeof CHART_UPDATES_AVAILABLE;
  project?: string;
}

export const Route = createFileRoute("/versions")({
  component: VersionsRoute,
  validateSearch: (raw: Record<string, unknown>): VersionsSearchInput => {
    const search: VersionsSearchInput = {
      projects: typeof raw.projects === "string" ? raw.projects : undefined,
      namespaces: typeof raw.namespaces === "string" ? raw.namespaces : undefined,
    };
    if (raw.view === CHART_UPDATES_AVAILABLE) {
      search.view = CHART_UPDATES_AVAILABLE;
    }
    if (typeof raw.project === "string" && raw.project.trim()) {
      search.project = raw.project.trim();
    }
    return search;
  },
});

function toRouteSearch(state: VersionSearch): VersionsSearchInput {
  return {
    projects: state.projects.length > 0 ? state.projects.join(",") : undefined,
    namespaces:
      state.namespaces.length > 0 ? state.namespaces.join(",") : undefined,
    view: state.view === CHART_UPDATES_AVAILABLE ? state.view : undefined,
    project: state.project || undefined,
  };
}

function VersionsRoute(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/versions" });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const apps = useApps();

  const state = useMemo(() => parseVersionSearch(search), [search]);

  useEffect(() => {
    const params = new URLSearchParams(searchStr);
    if (!params.has("view") || search.view === CHART_UPDATES_AVAILABLE) return;
    navigate({
      search: {
        projects: search.projects,
        namespaces: search.namespaces,
        project: search.project,
      },
      replace: true,
    });
  }, [
    navigate,
    search.namespaces,
    search.project,
    search.projects,
    search.view,
    searchStr,
  ]);

  const onFiltersChange = useCallback(
    (next: { projects: string[]; namespaces: string[] }) => {
      navigate({
        search: toRouteSearch({
          ...state,
          projects: next.projects,
          namespaces: next.namespaces,
        }),
        replace: true,
      });
    },
    [navigate, state],
  );

  const onProjectChange = useCallback(
    (project: string) => {
      navigate({
        search: toRouteSearch({ ...state, project }),
        replace: true,
      });
    },
    [navigate, state],
  );

  return (
    <ChartVersionsPage
      apps={apps.data}
      isLoading={apps.isLoading}
      error={apps.error}
      onRetry={() => {
        void apps.refetch();
      }}
      projects={state.projects}
      namespaces={state.namespaces}
      view={state.view}
      project={state.project}
      onFiltersChange={onFiltersChange}
      onProjectChange={onProjectChange}
    />
  );
}
